
/* ===== Page: Solutions – Index ===== */
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import MainLayout from '@/components/Layout/MainLayout';
import AnimateInView from '@/components/ui/animate-in-view';
import CaseStudies from '@/components/Solutions/CaseStudies';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import GoldSectionDivider from '@/components/ui/gold-section-divider';

/**
 * @component Solutions
 * @description Solutions overview page showcasing all security solutions
 * @returns {JSX.Element} Solutions page with premium styling and animations
 */
const solutions = [
  {
    id: 'fibre',
    title: 'Fiber-Optic In-Ground',
    description: 'A nearly invisible barrier that analyzes ground vibrations to detect humans, vehicles, or unauthorized movement up to 100km with AI-powered precision.',
    features: [
      'Invisible Barriers: Silent Precision - Uneen barriers protect without sacrificing aesthetics.',
      'AI-Powered Accuracy: Distinguishes between animals, weather, vehicles, and human intrusions.',
      'Enterprise-Ready Scale: Scale from small estates to 100km+ utility corridors.'
    ],
    image: '/images/solutions-fibre-hero-1.png',
    link: '/solutions/fibre',
    highlight: 'Most Advanced',
  },
  {
    id: 'fence',
    title: 'On-Fence Detection',
    description: 'Layers 5000 levels of sensitivity protection to fences, distinguishing between wind and breach attempts, ensuring early intrusion detection.',
    features: [
      'Patented Sensitivity: Detects vibrations as subtle as a branch brushing vs. a breach attempt.',
      '5000-Levels of Precision: Customizable alerts for different threat levels.',
      'Plug-and-Play: Retrofits existing fences; install in hours.',
      'Timeless Elegance: Retrofits existing boundaries with invisible sentinels.'
    ],
    image: '/public/images/solutions-fence-overview-section5.png',
    link: '/solutions/fence',
    highlight: 'Most Popular',
  },
  {
    id: 'bespoke',
    title: 'Bespoke Security',
    description: 'Custom solutions tailored for mixed properties, remote assets, and multi-use zones, designed with both efficacy and aesthetics in mind.',
    features: [
      'Mixed Properties: Blending above-ground/ground barriers for golf courses or estates.',
      'Remote Assets: Off-grid solar-powered sensors for pipelines or far-flung facilities.',
      'Multi-Use Zones: Separate high-security areas from public regions on private lands.'
    ],
    image: '/images/solutions-bespoke-case-studies-solar-farm-remote-2.png',
    link: '/solutions/bespoke',
    highlight: 'Fully Custom',
  },
];

// Added testimonial for CRO optimization
const testimonial = {
  quote: "The Forti-Fi system has given our family peace of mind knowing our property is secure without unsightly traditional security measures.",
  author: "Richard B., Estate Owner"
};

// FAQs for the solutions page
const faqs = [
  {
    question: "How do your fiber optic security systems detect intrusions?",
    answer: "Our fiber optic systems use advanced sensing technology that detects vibrations and acoustic signatures in the ground. When someone walks, drives, or digs near the protected area, these vibrations create unique patterns that our AI analyzes to determine if it's a threat or a false alarm. The system can distinguish between humans, animals, vehicles, and environmental factors with remarkable accuracy."
  },
  {
    question: "Can your systems work in extreme weather conditions?",
    answer: "Yes, our security solutions are engineered to perform reliably in all weather conditions. The fiber optic cables are sealed and protected against moisture, temperature extremes, and other environmental factors. Our on-fence detection systems are calibrated to distinguish between weather-induced movement and actual intrusion attempts, dramatically reducing false alarms even during storms or high winds."
  },
  {
    question: "What's the difference between your fiber optic and on-fence solutions?",
    answer: "Our fiber optic solutions are installed underground and provide an invisible security perimeter that detects intrusions through ground vibrations before anyone reaches your property line. The on-fence solution attaches to existing fences and walls to detect climbing, cutting, or tampering. Both systems use AI to analyze disturbances and can be used independently or together for comprehensive security coverage."
  },
  {
    question: "How customizable are your bespoke security solutions?",
    answer: "Our bespoke solutions are fully customizable to your specific property and security needs. We combine different technologies, adjust sensitivity levels for different zones, integrate with existing systems, and design solutions that respect the aesthetic of your property. Every installation begins with a thorough site assessment and security consultation to understand your unique requirements."
  },
  {
    question: "What kind of support and maintenance do you provide after installation?",
    answer: "We provide comprehensive support including system monitoring, regular maintenance checks, software updates, and 24/7 technical assistance. Our service plans include quarterly preventive maintenance visits, and our systems perform continuous self-diagnostics to identify and address potential issues before they affect performance."
  }
];

const Solutions = () => {
  return (
    <MainLayout>
      {/* Hero Section with gradient texture */}
      <section className="bg-gradient-to-r from-neutral-100 via-theme-solutions-bg to-forti-fi-background/30 py-16 px-4" aria-labelledby="solutions-heading">
        <div className="container mx-auto max-w-4xl text-center">
          <AnimateInView animation="fade-in">
            <h1 id="solutions-heading" className="text-black mb-4">
              Our Security Solutions
            </h1>
            <p className="text-forti-fi-charcoal text-xl md:text-2xl mb-6 max-w-3xl mx-auto">
              Advanced protection systems engineered with precision and implemented with discretion.
            </p>
          </AnimateInView>
        </div>
      </section>

      {/* Solutions Detail */}
      <section className="section bg-white py-12 md:py-16" aria-labelledby="solutions-detail-heading">
        <div className="container mx-auto">
          <h2 id="solutions-detail-heading" className="sr-only">Solution Details</h2>
          <div className="space-y-24">
            {solutions.map((solution, index) => (
              <AnimateInView 
                key={solution.id} 
                animation="slide-up"
                className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'md:grid-flow-dense' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                  <AnimateInView animation="fade-in" delay={200}>
                    {solution.highlight && (
                      <span className="inline-block bg-forti-fi text-white text-sm font-medium px-3 py-1 rounded-full mb-4">
                        {solution.highlight}
                      </span>
                    )}
                    <h2 className="text-black mb-4">{solution.title}</h2>
                    <p className="mb-4 text-lg text-forti-fi-charcoal">{solution.description}</p>
                    
                    <h3 className="text-black mb-3 text-lg">Key Features:</h3>
                    <ul className="mb-4 space-y-3">
                      {solution.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start group hover:bg-forti-fi-background/30 p-2 rounded transition-colors">
                          <CheckCircle2 className="text-forti-fi mr-2 h-5 w-5 shrink-0 mt-1 group-hover:scale-110 transition-transform" />
                          <span className="text-forti-fi-charcoal">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button asChild className="text-white group">
                      <Link to={solution.link}>
                        Explore {solution.title} <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </AnimateInView>
                </div>
                <Link 
                  to={solution.link}
                  className={`overflow-hidden rounded-lg hover-lift ${
                    index % 2 === 1 ? 'md:col-start-1' : ''
                  }`}
                >
                  <AnimateInView animation="fade-in" delay={400}>
                    <img 
                      src={solution.image} 
                      alt={solution.title} 
                      className="w-full h-full object-cover aspect-video hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </AnimateInView>
                </Link>
              </AnimateInView>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - New addition */}
      <section className="section bg-gradient-to-b from-white to-forti-fi-background/20 py-16" aria-labelledby="faq-heading">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col items-center mb-8">
            <GoldSectionDivider centered />
            <h2 id="faq-heading" className="text-center text-black mb-2">Frequently Asked Questions</h2>
          </div>
          <AnimateInView animation="fade-in">
            <div className="text-center mb-12">
              <p className="text-forti-fi-charcoal text-lg max-w-3xl mx-auto">
                Common questions about our security solutions and technologies.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-luxury p-6 md:p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-b border-forti-fi-lavender/30">
                    <AccordionTrigger className="text-forti-fi hover:text-forti-fi-light text-left font-medium py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-forti-fi-charcoal pt-2 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </AnimateInView>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="section bg-forti-fi-background py-16 md:py-16 pb-8" aria-labelledby="industries-overview-heading">
        <div className="container mx-auto">
          <div className="flex flex-col items-center mb-8">
            <GoldSectionDivider centered />
            <h2 id="industries-overview-heading" className="text-center text-black mb-2">Industries We Serve</h2>
          </div>
          <AnimateInView animation="fade-in">
            <p className="text-center text-forti-fi-charcoal text-lg max-w-3xl mx-auto mb-6">
              Our security solutions are adaptable to a wide range of industries, each with their unique challenges and requirements. From private estates to critical infrastructure, we provide tailored protection systems.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-forti-fi text-xl mb-3">Private Property</h3>
                <p className="text-forti-fi-charcoal mb-4">Premium security solutions for high-value private properties, estates, and luxury residences.</p>
                <Button asChild variant="outline" size="sm">
                  <Link to="/industries">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
              
              <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-forti-fi text-xl mb-3">Critical Infrastructure</h3>
                <p className="text-forti-fi-charcoal mb-4">Comprehensive protection for utilities, energy facilities, and essential service providers.</p>
                <Button asChild variant="outline" size="sm">
                  <Link to="/industries">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
              
              <div className="bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-forti-fi text-xl mb-3">Government & Healthcare</h3>
                <p className="text-forti-fi-charcoal mb-4">Advanced security measures for sensitive facilities with strict regulatory requirements.</p>
                <Button asChild variant="outline" size="sm">
                  <Link to="/industries">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            
            <div className="text-center">
              <Button asChild variant="gradient" className="text-white">
                <Link to="/industries">
                  View All Industries
                </Link>
              </Button>
            </div>
          </AnimateInView>
        </div>
      </section>

      {/* Case Studies Section */}
      <CaseStudies />

      {/* Testimonial section */}
      <section className="section bg-forti-fi-background py-12" aria-labelledby="testimonial-heading">
        <div className="container mx-auto max-w-3xl">
          <AnimateInView animation="scale-in">
            <div className="glass-card shadow-luxury p-6 md:p-8 relative">
              <div className="text-6xl text-forti-fi-teal absolute top-4 left-6 opacity-20">"</div>
              <div className="relative z-10">
                <p className="text-xl md:text-2xl italic mb-4 text-forti-fi-charcoal">
                  {testimonial.quote}
                </p>
                <p className="font-medium text-forti-fi-DEFAULT text-right">
                  — {testimonial.author}
                </p>
              </div>
            </div>
          </AnimateInView>
        </div>
      </section>

      {/* CTA Section with enhanced gradient */}
      <section className="section bg-gradient-to-r from-forti-fi-dark to-forti-fi-charcoal text-white py-12" aria-labelledby="contact-cta-heading">
        <div className="container mx-auto text-center max-w-3xl">
          <AnimateInView animation="fade-in">
            <h2 id="contact-cta-heading" className="mb-4 text-white">
              Ready to Secure Your Property?
            </h2>
            <p className="mb-6 text-forti-fi-lavender text-xl">
              Our security specialists are ready to provide a confidential consultation tailored to your unique requirements.
            </p>
            <Button asChild size="lg" variant="gradient" className="text-white">
              <Link to="/contact">
                Request a Consultation
              </Link>
            </Button>
          </AnimateInView>
        </div>
      </section>
    </MainLayout>
  );
};

export default Solutions;
